import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listTechnical, getTechnicalMeta, getTechnicalHistory, getExchangeFlows } from '../api';
import Disclaimer from '../components/Disclaimer';
import TechnicalChart from '../components/TechnicalChart';

const TREND_COLOR = {
  BULLISH: '#10b981',
  NEUTRAL: '#f59e0b',
  BEARISH: '#ef4444',
  UNKNOWN: '#64748b',
};
const TREND_LABEL_KEY = {
  BULLISH: 'technical.trendBullish',
  NEUTRAL: 'technical.trendNeutral',
  BEARISH: 'technical.trendBearish',
  UNKNOWN: 'technical.trendUnknown',
};

// Prices span BTC (~65,000) to micro-caps (~0.00001), so significant digits
// matter more than a fixed decimal count.
function fmtPrice(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n.toPrecision(4);
}
const fmtNum = (n, dp = 1) => (n == null || !Number.isFinite(n) ? '—' : n.toFixed(dp));
// Too little candle history for the long-lookback indicators — the stored
// composite/zone score are not a usable signal (see MIN_CANDLES backend).
const isInsufficient = (d) => d?.dataQuality?.sufficient === false && !d?.dataQuality?.provisional;
// 1D/3D history short but 4H/12H complete: a short-term trend read from
// those two only (dataQuality.shortComposite), labelled as provisional.
const isProvisional = (d) => d?.dataQuality?.sufficient === false && !!d?.dataQuality?.provisional;
const scoreOf = (d) => (isProvisional(d) ? d.dataQuality.shortComposite : d.composite);

function SideScore({ d }) {
  const { t } = useTranslation();
  if (isInsufficient(d)) {
    return <span style={{ ...s.sideScoreGroup, fontSize: 11, fontWeight: 700, color: '#f59e0b' }} title={t('technical.insufficientShort')}>⚠ N/A</span>;
  }
  if (isProvisional(d)) {
    return <span style={s.sideScoreGroup} title={t('technical.provisionalShort')}><span style={{ ...s.sideScore, color: TREND_COLOR[d.trend] }}>⏳{fmtNum(scoreOf(d), 0)}</span></span>;
  }
  return (
    <span style={s.sideScoreGroup}>
      <span style={{ ...s.sideScore, color: TREND_COLOR[d.trend] }}>{fmtNum(d.composite, 0)}</span>
      <ZoneBadge zoneScore={d.zoneScore} />
    </span>
  );
}

function InsufficientDataBanner({ d }) {
  const { t } = useTranslation();
  if (!isInsufficient(d) && !isProvisional(d)) return null;
  const dq = d.dataQuality;
  const TF = { h4: '4H', h12: '12H', d1: '1D', d3: '3D' };
  const details = (dq.shortfalls || []).map((k) => `${TF[k] || k}: ${dq.candleCounts?.[k] ?? '?'}/${dq.minRequired?.[k] ?? '?'}`).join(', ');
  if (isProvisional(d)) {
    const ready = dq.fullReadyAt ? new Date(dq.fullReadyAt).toISOString().slice(0, 10) : '—';
    return (
      <div style={s.insufficientBanner}>
        <strong>⏳ {t('technical.provisionalTitle')}</strong>
        <div>{t('technical.provisionalBody', { details, source: dq.source === 'bybit' ? 'Bybit' : 'Binance', ready })}</div>
      </div>
    );
  }
  return (
    <div style={s.insufficientBanner}>
      <strong>⚠ {t('technical.insufficientTitle')}</strong>
      <div>{t('technical.insufficientBody', { details, source: dq.source === 'bybit' ? 'Bybit' : 'Binance' })}</div>
    </div>
  );
}
const fmtPct = (n) => (n == null || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`);
// Compact USD (for order-book depth / traded value).
function fmtUsdC(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}
const HEALTH_COLOR = { excellent: '#10b981', good: '#22c55e', fair: '#f59e0b', thin: '#ef4444' };

// Small Green/Red Zone badge shown inline right after a ticker's composite
// score (separated by a "/" colored to match) in the left sidebar lists —
// only rendered when one side is actually dominant (>50), matching
// ZoneScoreBar's raw-color-name convention (no VI/EN translation, since
// "Green"/"Red" here are score-band names, not prose).
function ZoneBadge({ zoneScore }) {
  if (!zoneScore || zoneScore.green == null) return null;
  const { green, red } = zoneScore;
  const label = green > 50 ? 'Green' : red > 50 ? 'Red' : null;
  if (!label) return null;
  const color = label === 'Green' ? '#10b981' : '#ef4444';
  const value = label === 'Green' ? green : red;
  return (
    <>
      <span style={{ color, fontWeight: 800 }}>/</span>
      <span style={{ ...s.sideZone, color }}>{label} {fmtNum(value, 0)}</span>
    </>
  );
}

// Subtract calendar days from a 'YYYY-MM-DD' string (UTC math avoids DST drift).
function shiftDate(dateStr, days) {
  const [y, m, dd] = String(dateStr).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, dd));
  dt.setUTCDate(dt.getUTCDate() - days);
  return dt.toISOString().slice(0, 10);
}

// Shared sidebar (ticker list + search) + main report panel — used by both
// the live "Phân tích hiện tại" tab and the "Lịch sử báo cáo" tab, just fed
// a different dataset (today's live docs vs. one past date's docs).
function ReportBrowser({ items, ready, selected, onSelect, q, onQChange, history, exchangeFlows }) {
  const { t } = useTranslation();
  const selectedDoc = items.find(d => d.symbol === selected) || null;
  const filtered = q
    ? items.filter(d => (d.base || d.symbol).toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  if (!ready) return <div style={s.loading}>{t('technical.loadingData')}</div>;
  if (items.length === 0) {
    return (
      <div style={s.empty}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📈</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          {t('technical.noAnalysisData')}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {t('technical.autoGenNote')}
        </div>
      </div>
    );
  }

  return (
    <div style={s.layout}>
      {/* Left sidebar — project ticker list */}
      <aside style={s.sidebar}>
        <input
          style={s.sideSearch}
          placeholder={t('technical.searchPlaceholder')}
          value={q}
          onChange={e => onQChange(e.target.value)}
        />
        <div style={s.sideList}>
          {filtered.map(d => {
            const active = d.symbol === selected;
            return (
              <button
                key={d.symbol}
                style={active ? s.sideItemActive : s.sideItem}
                onClick={(e) => { onSelect(d.symbol); e.currentTarget.blur(); }}
              >
                <span style={s.sideTop}>
                  <span style={s.sideTicker}>{d.base || d.symbol}</span>
                  <SideScore d={d} />
                </span>
                <span style={s.sideBottom}>
                  <span style={{ color: TREND_COLOR[d.trend], fontWeight: 700 }}>{isInsufficient(d) ? t('technical.insufficientShort') : isProvisional(d) ? `${t(TREND_LABEL_KEY[d.trend])} · ${t('technical.provisionalTag')}` : t(TREND_LABEL_KEY[d.trend])}</span>
                  <span style={s.sidePrice}>${fmtPrice(d.price)}</span>
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div style={s.sideEmpty}>{t('technical.noProjectFound')}</div>
          )}
        </div>
      </aside>

      {/* Main — full report for the selected project */}
      <main style={s.main}>
        {selectedDoc
          ? <DetailPanel d={selectedDoc} history={history} exchangeFlows={exchangeFlows} />
          : <div style={s.loading}>{t('technical.selectProject')}</div>}
      </main>
    </div>
  );
}

export default function TechnicalPage() {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'history' | 'charts'

  // ── Tab 1: live/current analysis ──────────────────────────────────────
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // symbol
  const [history, setHistory] = useState([]);
  const [exchangeFlows, setExchangeFlows] = useState(null); // { date, rows, unsupported }

  useEffect(() => { getTechnicalMeta().then(setMeta).catch(() => {}); }, []);

  // Refreshed server-side every 4h — poll on the same cadence rather than
  // just once, so a tab left open picks up the next cron run automatically.
  useEffect(() => {
    const load = () => getExchangeFlows().then(setExchangeFlows).catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setReady(false);
    try {
      const { docs } = await listTechnical();
      setItems(docs || []);
      setSelected(prev => (prev && docs.some(d => d.symbol === prev) ? prev : (docs[0]?.symbol || null)));
    } catch { /* silent */ }
    finally { setReady(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // History for the selected symbol powers the market-trend comparison table.
  useEffect(() => {
    if (!selected) { setHistory([]); return undefined; }
    let active = true;
    getTechnicalHistory(selected, { limit: 30 })
      .then(r => { if (active) setHistory(r.docs || []); })
      .catch(() => { if (active) setHistory([]); });
    return () => { active = false; };
  }, [selected]);

  // ── Tab 2: past-day report browser ────────────────────────────────────
  const [histDate, setHistDate] = useState(null);
  const [histItems, setHistItems] = useState([]);
  const [histReady, setHistReady] = useState(false);
  const [histQ, setHistQ] = useState('');
  const [histSelected, setHistSelected] = useState(null);
  const [histTrend, setHistTrend] = useState([]);

  // Everything below is gated on activeTab === 'history' — this tab's data
  // (a full extra day's worth of docs, plus a 30-day trend series per
  // symbol picked) used to fetch eagerly as soon as `meta` arrived,
  // regardless of which tab was open, doubling the data loaded on every
  // page visit even for users who never open "Lịch sử báo cáo". Now it
  // only starts the first time the tab is actually opened, and stays
  // cached in state after that (switching tabs back and forth doesn't
  // re-fetch).

  // Default to the most recent PAST date once the history tab is first
  // opened (today is already covered by the "Phân tích hiện tại" tab).
  useEffect(() => {
    if (activeTab !== 'history' || !meta?.dates?.length || histDate) return;
    setHistDate(meta.dates.find(d => d !== meta.today) || meta.dates[0]);
  }, [activeTab, meta, histDate]);

  useEffect(() => {
    if (activeTab !== 'history' || !histDate) return undefined;
    let active = true;
    setHistReady(false);
    listTechnical({ date: histDate })
      .then(({ docs }) => {
        if (!active) return;
        setHistItems(docs || []);
        setHistSelected(prev => (prev && docs.some(d => d.symbol === prev) ? prev : (docs[0]?.symbol || null)));
      })
      .catch(() => { if (active) setHistItems([]); })
      .finally(() => { if (active) setHistReady(true); });
    return () => { active = false; };
  }, [activeTab, histDate]);

  useEffect(() => {
    if (activeTab !== 'history' || !histSelected || !histDate) { return undefined; }
    let active = true;
    getTechnicalHistory(histSelected, { limit: 30, dateTo: histDate })
      .then(r => { if (active) setHistTrend(r.docs || []); })
      .catch(() => { if (active) setHistTrend([]); });
    return () => { active = false; };
  }, [activeTab, histSelected, histDate]);

  // ── Tab 3: historical indicator charts ─────────────────────────────────
  const [chartsSymbol, setChartsSymbol] = useState(null);
  const [chartsData, setChartsData] = useState([]);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    if (activeTab !== 'charts' || chartsSymbol || !items.length) return;
    setChartsSymbol(selected && items.some(d => d.symbol === selected) ? selected : items[0].symbol);
  }, [activeTab, chartsSymbol, items, selected]);

  useEffect(() => {
    if (activeTab !== 'charts' || !chartsSymbol) return undefined;
    let active = true;
    setChartsReady(false);
    // Up to 6 months back — a chart only draws the days that actually have
    // data for a given metric (see ChartsView), so a symbol newer than 6
    // months just shows a shorter series instead of an error.
    getTechnicalHistory(chartsSymbol, { limit: 183, slim: true })
      .then(r => { if (active) setChartsData(r.docs || []); })
      .catch(() => { if (active) setChartsData([]); })
      .finally(() => { if (active) setChartsReady(true); });
    return () => { active = false; };
  }, [activeTab, chartsSymbol]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>{t('technical.title')}</h2>
        <p style={s.subtitle}>
          {meta
            ? t('technical.subtitleReady', { count: meta.totalSymbols })
            : t('technical.subtitleLoading')}
        </p>
      </div>

      <div style={s.notice}>
        {t('technical.notice')}
      </div>

      <div style={s.tabBar}>
        <button
          onClick={(e) => { setActiveTab('current'); e.currentTarget.blur(); }}
          style={activeTab === 'current' ? s.tabBtnActive : s.tabBtn}
        >
          {t('technical.tabCurrent')}
        </button>
        <button
          onClick={(e) => { setActiveTab('history'); e.currentTarget.blur(); }}
          style={activeTab === 'history' ? s.tabBtnActive : s.tabBtn}
        >
          {t('technical.tabHistory')}
        </button>
        <button
          onClick={(e) => { setActiveTab('charts'); e.currentTarget.blur(); }}
          style={activeTab === 'charts' ? s.tabBtnActive : s.tabBtn}
        >
          {t('technical.tabCharts')}
        </button>
      </div>

      {activeTab === 'current' ? (
        <ReportBrowser
          items={items} ready={ready} selected={selected} onSelect={setSelected}
          q={q} onQChange={setQ} history={history} exchangeFlows={exchangeFlows}
        />
      ) : activeTab === 'history' ? (
        <>
          <div style={s.histDateBar}>
            <span style={s.filterLabel}>{t('technical.reportDateLabel')}</span>
            {!meta?.dates?.length ? (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('technical.noHistoryData')}</span>
            ) : (
              <select
                style={s.histDateSelect}
                value={histDate || ''}
                onChange={(e) => setHistDate(e.target.value)}
              >
                {meta.dates.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
          </div>
          <ReportBrowser
            items={histItems} ready={histReady} selected={histSelected} onSelect={setHistSelected}
            q={histQ} onQChange={setHistQ} history={histTrend} exchangeFlows={null}
          />
        </>
      ) : (
        <ChartsView
          items={items} symbol={chartsSymbol} onSelect={setChartsSymbol}
          data={chartsData} ready={chartsReady}
        />
      )}
    </div>
  );
}

// Market-trend comparison across fixed lookback points. Only points that
// actually exist in the DB are shown; if no past point is available the whole
// section is skipped (there is nothing to compare against yet).
const TREND_POINTS = [
  [0, 'pointToday'],
  [1, 'pointYesterday'],
  [3, 3],
  [7, 7],
  [10, 10],
  [15, 15],
];

function MarketTrendSection({ no, current, history }) {
  const { t } = useTranslation();
  const byDate = new Map((history || []).map(h => [h.date, h]));
  // The current report may be newer than the newest history row — index it too.
  if (current?.date) byDate.set(current.date, current);

  const currentComposite = current?.composite;
  const currentPrice = current?.price;
  const rows = TREND_POINTS
    .map(([offset, labelKeyOrDays]) => {
      const date = shiftDate(current.date, offset);
      const doc = byDate.get(date);
      if (!doc) return null;
      const label = typeof labelKeyOrDays === 'string'
        ? t(`technical.${labelKeyOrDays}`)
        : t('technical.pointDaysAgo', { count: labelKeyOrDays });
      return { offset, label, date, trend: doc.trend, composite: doc.composite, price: doc.price };
    })
    .filter(Boolean);

  // Need at least one past point beyond "today" for a meaningful comparison.
  if (rows.length < 2) return null;

  return (
    <ReportSection no={no} title={t('technical.trendSectionTitle')}>
      <p style={s.sectionText}>
        {t('technical.trendSectionDesc')}
      </p>
      <p style={{ ...s.formula, marginTop: 0, marginBottom: 8 }}>
        {t('technical.trendSectionFormula')}
      </p>
      <div style={s.dtWrap}>
        <div style={{ ...s.dtRow, ...s.dtHead }}>
          <span style={{ ...s.dtCell, flex: '0 0 22%' }}>{t('technical.colTime')}</span>
          <span style={{ ...s.dtCell, flex: '0 0 16%' }}>{t('technical.colDate')}</span>
          <span style={s.dtCell}>{t('technical.colStatus')}</span>
          <span style={{ ...s.dtCell, flex: '0 0 26%' }}>{t('technical.colPriceDelta')}</span>
          <span style={{ ...s.dtCell, flex: '0 0 16%' }}>{t('technical.colScoreDelta')}</span>
        </div>
        {rows.map(r => {
          const delta = (Number.isFinite(r.composite) && Number.isFinite(currentComposite) && r.offset !== 0)
            ? r.composite - currentComposite : null;
          // Price move from that point to now: positive = price has risen since.
          const pricePct = (Number.isFinite(r.price) && Number.isFinite(currentPrice) && r.price > 0 && r.offset !== 0)
            ? ((currentPrice - r.price) / r.price) * 100 : null;
          const pctColor = pricePct == null ? 'var(--text-dim)' : pricePct > 0 ? TREND_COLOR.BULLISH : pricePct < 0 ? TREND_COLOR.BEARISH : 'var(--text-dim)';
          return (
            <div key={r.offset} style={{ ...s.dtRow, ...(r.offset === 0 ? s.dtRowHi : {}) }}>
              <span style={{ ...s.dtCell, flex: '0 0 22%', color: 'var(--text)', fontWeight: 600 }}>{r.label}</span>
              <span style={{ ...s.dtCell, flex: '0 0 16%', fontVariantNumeric: 'tabular-nums' }}>{r.date}</span>
              <span style={{ ...s.dtCell, color: TREND_COLOR[r.trend], fontWeight: 700 }}>{t(TREND_LABEL_KEY[r.trend])}</span>
              <span style={{ ...s.dtCell, flex: '0 0 26%', fontVariantNumeric: 'tabular-nums' }}>
                ${fmtPrice(r.price)}
                {pricePct != null && (
                  <span style={{ color: pctColor, fontSize: 11, fontWeight: 700 }}>
                    {' '}({pricePct > 0 ? '+' : ''}{pricePct.toFixed(2)}%)
                  </span>
                )}
              </span>
              <span style={{ ...s.dtCell, flex: '0 0 16%', fontVariantNumeric: 'tabular-nums' }}>
                {fmtNum(r.composite, 1)}
                {delta != null && (
                  <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                    {' '}({delta > 0 ? '+' : ''}{delta.toFixed(1)})
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </ReportSection>
  );
}

// Report block: a numbered heading, optional AI commentary paragraphs, then
// optional data (table/bars). Mirrors the docx section layout.
function ReportSection({ no, title, commentary, children }) {
  const paras = Array.isArray(commentary) ? commentary.filter(Boolean) : (commentary ? [commentary] : []);
  return (
    <div style={s.section}>
      <div style={s.sectionHeading}>{no}. {title}</div>
      {paras.map((p, idx) => <p key={idx} style={s.sectionText}>{p}</p>)}
      {children}
    </div>
  );
}

// Score row with a proportional fill bar, like the docx's ███████░░░ bars.
function ScoreBar({ label, score, weight, highlight }) {
  const pct = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
  const color = pct >= 62 ? TREND_COLOR.BULLISH : pct >= 45 ? TREND_COLOR.NEUTRAL : TREND_COLOR.BEARISH;
  return (
    <div style={{ ...s.scoreRow, ...(highlight ? s.scoreRowHi : {}) }}>
      <span style={s.scoreLabel}>{label}{weight ? <span style={s.scoreWeight}> ×{weight}</span> : null}</span>
      <span style={s.scoreTrack}>
        <span style={{ ...s.scoreFill, width: `${pct}%`, background: color }} />
      </span>
      <span style={{ ...s.scoreVal, color }}>{Number.isFinite(score) ? score.toFixed(0) : '—'}</span>
    </div>
  );
}

// A docx-style 3-column table: item | value | meaning.
function DataTable({ head, rows }) {
  return (
    <div style={s.dtWrap}>
      <div style={{ ...s.dtRow, ...s.dtHead }}>
        {head.map((h, i) => <span key={i} style={{ ...s.dtCell, ...(i === 0 ? s.dtCellFirst : {}), ...(i === 1 ? s.dtCellVal : {}) }}>{h}</span>)}
      </div>
      {rows.map((r, ri) => (
        <div key={ri} style={s.dtRow}>
          {r.map((c, ci) => (
            <span key={ci} style={{ ...s.dtCell, ...(ci === 0 ? s.dtCellFirst : {}), ...(ci === 1 ? s.dtCellVal : {}) }}>{c}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

const yesNo = (t, v) => (v == null ? '—' : v ? t('technical.above') : t('technical.below'));
const ICHI_KEY = { above: 'technical.ichiAbove', inside: 'technical.ichiInside', below: 'technical.ichiBelow' };

const ZONE_FACTOR_KEY = {
  oversold: 'technical.zoneFactorOversold',
  support: 'technical.zoneFactorSupport',
  bounceRate: 'technical.zoneFactorBounceRate',
  volumeFlow: 'technical.zoneFactorVolumeFlow',
  exchangeFlow: 'technical.zoneFactorExchangeFlow',
  orderBook: 'technical.zoneFactorOrderBook',
};

// Green / Red — a contrarian accumulation-vs-distribution read, deliberately
// separate from the trend-following composite score above. green + red
// always = 100. Computed once/day alongside the AI report — see
// backend/services/zoneScoreService.js for the full methodology and the
// capital-preservation guardrail behind `riskFlag`.
// Labelled with the raw color names only (no "gom"/"bán" wording) — this
// report intentionally avoids any buy/sell-flavored language.
function ZoneScoreBar({ zoneScore }) {
  const { t } = useTranslation();
  if (!zoneScore || zoneScore.green == null) {
    return (
      <div style={s.flowStrip}>
        <span style={s.flowStripTitle}>{t('technical.zoneTitle')}</span>
        <span style={s.flowStripNote}>{t('technical.zoneNotEnoughData')}</span>
      </div>
    );
  }

  const { green, red, confidence, riskFlag, riskFlagVi } = zoneScore;
  const factors = zoneScore.factors;
  const zoneLabel = green >= 70 ? 'GREEN' : red >= 70 ? 'RED' : t('technical.zoneNeutral');
  const zoneColor = green >= 70 ? '#10b981' : red >= 70 ? '#ef4444' : 'var(--text-muted)';
  // riskFlagVi is a dynamically-generated guardrail sentence from the backend
  // (backend/services/zoneScoreService.js) — not yet translated server-side,
  // so it's shown as-is (Vietnamese) regardless of UI language for now.
  const riskText = riskFlagVi;

  return (
    <div style={s.zonePanel}>
      <div style={s.zoneHeader}>
        <span style={s.flowStripTitle}>{t('technical.zoneTitle')}</span>
        <span style={{ ...s.zoneBadge, color: zoneColor, borderColor: zoneColor + '55', background: zoneColor + '14' }}>
          {zoneLabel}
        </span>
        {confidence != null && confidence < 60 && (
          <span style={{ fontSize: 10.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {t('technical.zoneLowConfidence', { pct: confidence })}
          </span>
        )}
      </div>

      <div style={s.zoneBarTrack}>
        <div style={{ ...s.zoneBarFill, width: `${green}%`, background: '#10b981' }} title={`Green: ${green}`} />
        <div style={{ ...s.zoneBarFill, width: `${red}%`, background: '#ef4444' }} title={`Red: ${red}`} />
      </div>
      <div style={s.zoneBarLabels}>
        <span style={{ color: '#10b981', fontWeight: 700 }}>🟢 Green {green}</span>
        <span style={{ color: '#ef4444', fontWeight: 700 }}>🔴 Red {red}</span>
      </div>

      {riskFlag && (
        <div style={s.zoneRiskBox}>⚠️ {riskText}</div>
      )}

      <div style={s.zoneFactors}>
        {/* N/A factors (e.g. exchange flow for a pair with no coverage) are
            already excluded from the score itself server-side (weight
            rescaled among the rest) — skip rendering them too instead of
            showing a "no data" row for a metric that isn't being used. */}
        {factors.filter((f) => !f.na).map((f) => (
          <div key={f.name} style={s.zoneFactorRow}>
            <span style={s.zoneFactorLabel}>{ZONE_FACTOR_KEY[f.name] ? t(ZONE_FACTOR_KEY[f.name]) : f.name}</span>
            <span style={s.zoneFactorTrack}>
              <span style={{ ...s.zoneFactorFill, width: `${Math.round(f.fraction * 100)}%` }} />
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)', width: 32, textAlign: 'right' }}>{Math.round(f.fraction * 100)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// "How much capital would it take to fake this much volume?" — a rough
// wash-trading cost estimate, not a real forensic signal. Method: split the
// day's total dollar volume evenly across every trade that happened
// (quoteVolume / tradeCount) to get an "average order size" — that's the
// starting float you recycle (buy, then immediately sell the same size,
// repeat). The notional itself round-trips back to you each cycle; only the
// per-order fee is a permanent loss, and it's deducted on EVERY one of the
// day's tradeCount orders, not just one pair — so the total fee drain that
// has to be topped up over the whole day is the fee rate × the ENTIRE day's
// volume (quoteVolume), not the fee rate × one order. Net balance needed =
// that starting float + the day's total fee drain. A low number here means
// the day's reported volume could technically be reproduced with very
// little capital (cheap to wash-trade) — it does NOT mean it WAS wash-traded.
// 0.1% matches Binance's standard (non-BNB-discounted) spot taker fee — the
// data this whole page's flow/volume figures are themselves sourced from,
// so it's the realistic worst-case rate for whoever generated that volume.
const WASH_FEE_RATE = 0.001; // 0.1% per order

// K/M/B-compacted formatting (fmtUsdC, or even fmtUsdPrecise's cents-below-
// $1000 version) rounds two of this box's numbers to the same "$1.61M" even
// when they differ by a few hundred dollars — exactly the kind of gap this
// box exists to show (fee vs. net balance). Always show the full figure
// with thousands separators and cents instead of compacting.
function fmtUsdFull(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function WashCostBox({ fl }) {
  const { t } = useTranslation();
  if (!Number.isFinite(fl.quoteVolume) || !Number.isFinite(fl.tradeCount) || fl.tradeCount <= 0 || !Number.isFinite(fl.totalVolume) || fl.totalVolume <= 0) return null;

  // A round trip needs an equal buy leg and sell leg — you can't wash-trade
  // more on one side than the other. So only the BALANCED portion (bounded
  // by whichever side, buy or sell, is smaller) is wash-tradeable; whatever
  // volume sits beyond that on the larger side has no offsetting order and
  // is real, one-directional flow, not a candidate for wash trading.
  // buyVolume/sellVolume are stored in native units — split quoteVolume
  // (USD) between the two sides proportionally to approximate each side's
  // USD value (assumes similar average price on both sides intraday).
  const buyVolumeUsd = fl.quoteVolume * (fl.buyVolume / fl.totalVolume);
  const sellVolumeUsd = fl.quoteVolume * (fl.sellVolume / fl.totalVolume);
  const washVolumeUsd = 2 * Math.min(buyVolumeUsd, sellVolumeUsd);
  const realVolumeUsd = fl.quoteVolume - washVolumeUsd;

  const avgOrderSizeUsd = fl.quoteVolume / fl.tradeCount;
  // Fee is deducted per order, on ALL of the day's trades — summed across
  // every order that's exactly WASH_FEE_RATE × the wash-tradeable volume
  // (the balanced portion only, not the real directional excess above).
  const totalFeesUsd = washVolumeUsd * WASH_FEE_RATE;
  const netBalanceUsd = avgOrderSizeUsd + totalFeesUsd;

  return (
    <div style={s.washBox}>
      <div style={s.washTitle}>{t('technical.washTitle')}</div>
      <p style={s.washDesc}>{t('technical.washDesc')}</p>
      <div style={s.washRow}>
        <span style={s.washLabel}>{t('technical.washWashVolume')}</span>
        <span style={s.washValue}>{fmtUsdFull(washVolumeUsd)}</span>
      </div>
      <div style={s.washRow}>
        <span style={s.washLabel}>{t('technical.washRealVolume')}</span>
        <span style={s.washValue}>{fmtUsdFull(realVolumeUsd)}</span>
      </div>
      <div style={s.washRow}>
        <span style={s.washLabel}>{t('technical.washAvgOrder')}</span>
        <span style={s.washValue}>{fmtUsdFull(avgOrderSizeUsd)}</span>
      </div>
      <div style={s.washRow}>
        <span style={s.washLabel}>{t('technical.washFee', { count: fl.tradeCount.toLocaleString('en-US') })}</span>
        <span style={s.washValue}>{fmtUsdFull(totalFeesUsd)}</span>
      </div>
      <div style={s.washRow}>
        <span style={s.washLabel}>{t('technical.washNetBalance')}</span>
        <span style={{ ...s.washValue, color: 'var(--accent)', fontSize: 15 }}>{fmtUsdFull(netBalanceUsd)}</span>
      </div>
      <p style={s.washNote}>{t('technical.washNote')}</p>
    </div>
  );
}

// Cumulative-so-far-today token deposits into (nạp lên sàn) vs withdrawals
// out of (rút khỏi sàn) exchange-labeled wallets — refreshed every 4h
// server-side, independent of the once-daily AI report above/below it. Not
// every tracked symbol has coverage — shown as an explicit note rather than
// a silent gap.
function ExchangeFlowStrip({ base, exchangeFlows }) {
  const { t } = useTranslation();
  if (!exchangeFlows) return null;
  const row = (exchangeFlows.rows || []).find((r) => r.base === base);
  const isUnsupported = (exchangeFlows.unsupported || []).includes(base);

  if (!row) {
    return (
      <div style={s.flowStrip}>
        <span style={s.flowStripTitle}>{t('technical.exchangeFlowTitle')}</span>
        <span style={s.flowStripNote}>
          {isUnsupported
            ? t('technical.exchangeFlowNoData', { base })
            : t('technical.exchangeFlowWaiting')}
        </span>
      </div>
    );
  }

  const netColor = row.netUsd >= 0 ? '#10b981' : '#ef4444';

  // Some tokens (SOL) only have a single net figure available — no reliable
  // way to split it into separate nạp/rút — shown as one number instead of
  // implying a false 0/0 split.
  if (row.netOnly) {
    return (
      <div style={s.flowStrip}>
        <span style={s.flowStripTitle}>{t('technical.exchangeFlowTitle')}</span>
        <span style={{ ...s.flowStripItem, color: netColor, fontWeight: 700 }}>
          {t('technical.exchangeFlowNet')} {row.netUsd >= 0 ? '+' : ''}{fmtUsdC(row.netUsd)} {row.netUsd >= 0 ? t('technical.exchangeFlowNetInflow') : t('technical.exchangeFlowNetOutflow')}
        </span>
        <span style={s.flowStripNote}>{t('technical.exchangeFlowNetOnlyNote')}</span>
      </div>
    );
  }

  return (
    <div style={s.flowStrip}>
      <span style={s.flowStripTitle}>{t('technical.exchangeFlowTitle')}</span>
      <span style={s.flowStripItem}>
        <span style={{ color: '#10b981', fontWeight: 700 }}>{t('technical.exchangeFlowInflow')}</span> {fmtUsdC(row.inflowUsd)}
      </span>
      <span style={s.flowStripItem}>
        <span style={{ color: '#ef4444', fontWeight: 700 }}>{t('technical.exchangeFlowOutflow')}</span> {fmtUsdC(row.outflowUsd)}
      </span>
      <span style={{ ...s.flowStripItem, color: netColor, fontWeight: 700 }}>
        {t('technical.exchangeFlowNet')} {row.netUsd >= 0 ? '+' : ''}{fmtUsdC(row.netUsd)}
      </span>
      <span style={s.flowStripNote}>{t('technical.exchangeFlowNote')}</span>
    </div>
  );
}

function DetailPanel({ d, history, exchangeFlows }) {
  const { t, i18n } = useTranslation();
  const k = d.keyLevels || {};
  const st = d.structure || {};
  const i = d.indicators?.d1 || {};
  // narrativeEn is a translated sibling of `narrative` on the same doc (not
  // a separate document — see backend TechnicalAnalysis model) — fall back
  // to the Vietnamese version if the English translation hasn't landed yet
  // for this particular day/symbol.
  const nar = (i18n.language === 'en' && d.narrativeEn?.summary) ? d.narrativeEn : (d.narrative || {});
  const liq = d.liquidity;
  const fl = d.flow;
  const of = d.orderFlow;
  const price = d.price;
  const hasNarrative = !!nar.summary;

  const rel = (level) => {
    if (!Number.isFinite(price) || !Number.isFinite(level)) return '';
    return price >= level ? t('technical.relAbove') : t('technical.relBelow');
  };

  // st.macdStateVi / adxRegimeVi / emaCrossVi / obvTrendVi and liq.healthVi
  // are dynamically-computed Vietnamese evaluation strings from the backend
  // (technicalService.js) — not yet translated server-side, shown as-is
  // regardless of UI language for now (same as zoneScore's riskFlagVi).
  const evalOverboughtOversoldNeutral = (isOver, isOver2) => (isOver ? t('technical.evalOverbought') : isOver2 ? t('technical.evalOversold') : t('technical.evalNeutral'));

  return (
    <div style={s.report}>
      <InsufficientDataBanner d={d} />

      <TechnicalChart symbol={d.symbol} keyLevels={d.keyLevels} />

      {!isInsufficient(d) && !isProvisional(d) && <ZoneScoreBar zoneScore={d.zoneScore} />}

      <ExchangeFlowStrip base={d.base || d.symbol} exchangeFlows={exchangeFlows} />

      {d.narrativeError && !hasNarrative && (
        <div style={s.narrativeMissing}>
          {t('technical.narrativeMissing')}
        </div>
      )}

      {/* 1 — Market summary */}
      <ReportSection no={1} title={t('technical.section1Title')} commentary={nar.summary}>
        <div style={s.summaryLine}>
          {t('technical.summaryLine', { date: d.date, price: fmtPrice(price) })}
          {isInsufficient(d)
            ? <span style={{ color: '#f59e0b', fontWeight: 700 }}> {t('technical.insufficientShort')}</span>
            : isProvisional(d)
              ? <span style={{ color: TREND_COLOR[d.trend], fontWeight: 700 }}> {t(TREND_LABEL_KEY[d.trend])} · {t('technical.provisionalTag')} ({fmtNum(scoreOf(d), 1)}/100)</span>
              : <span style={{ color: TREND_COLOR[d.trend], fontWeight: 700 }}> {t(TREND_LABEL_KEY[d.trend])} ({fmtNum(d.composite, 1)}/100)</span>}
        </div>
      </ReportSection>

      {/* 2 — Market-trend comparison (only if enough history exists) */}
      <MarketTrendSection no={2} current={d} history={history} />

      {/* 3 — Timeframe scores */}
      <ReportSection no={3} title={t('technical.section3Title')} commentary={nar.trendAnalysis}>
        <ScoreBar label={t('technical.tf4h')} score={d.scores?.h4} weight="0.10" />
        <ScoreBar label={t('technical.tf12h')} score={d.scores?.h12} weight="0.15" />
        <ScoreBar label={t('technical.tf1d')} score={d.scores?.d1} weight="0.45" />
        <ScoreBar label={t('technical.tf3d')} score={d.scores?.d3} weight="0.30" />
        <ScoreBar label={isProvisional(d) ? t('technical.tfShortTotal') : t('technical.tfTotal')} score={scoreOf(d)} highlight />
        <div style={s.formula}>{t('technical.scoreFormula')}</div>
      </ReportSection>

      {/* 4 — Key levels */}
      <ReportSection no={4} title={t('technical.section4Title')} commentary={nar.keyLevelsCommentary}>
        <DataTable
          head={[t('technical.colLevel'), t('technical.colPrice'), t('technical.colMeaning')]}
          rows={[
            [t('technical.levelCurrentPrice'), `$${fmtPrice(price)}`, t('technical.meaningCurrentPrice')],
            [t('technical.levelEma50'), `$${fmtPrice(k.ema50_1d)}`, t('technical.meaningEma50', { rel: rel(k.ema50_1d) })],
            [t('technical.levelEma200'), `$${fmtPrice(k.ema200_1d)}`, t('technical.meaningEma200', { rel: rel(k.ema200_1d) })],
            [t('technical.levelMonthResistance'), `$${fmtPrice(k.monthResistance)}`, t('technical.meaningMonthResistance', { pct: fmtPct(st.pctToMonthResistance) })],
            [t('technical.levelMonthSupport'), `$${fmtPrice(k.monthSupport)}`, t('technical.meaningMonthSupport', { pct: fmtPct(st.pctToMonthSupport) })],
            [t('technical.levelPrevMonthHiLo'), `$${fmtPrice(k.prevMonthHigh)} / $${fmtPrice(k.prevMonthLow)}`, t('technical.meaningPrevMonthHiLo')],
            [t('technical.levelBbUpper'), `$${fmtPrice(k.bbUpper_1d)}`, t('technical.meaningBbUpper')],
            [t('technical.levelBbLower'), `$${fmtPrice(k.bbLower_1d)}`, t('technical.meaningBbLower')],
            [t('technical.levelAtr'), `$${fmtPrice(k.atr_1d)}`, t('technical.meaningAtr')],
            [t('technical.levelFib618'), `$${fmtPrice(k.fib618)}`, t('technical.meaningFib618')],
            [t('technical.levelFib786'), `$${fmtPrice(k.fib786)}`, t('technical.meaningFib786')],
            [t('technical.levelSwingHiLo'), `$${fmtPrice(k.swingHigh)} / $${fmtPrice(k.swingLow)}`, t('technical.meaningSwingHiLo')],
          ]}
        />
      </ReportSection>

      {/* 5 — 1D indicator table */}
      <ReportSection no={5} title={t('technical.section5Title')} commentary={[nar.momentum, nar.volumeFlow, nar.volatility]}>
        <DataTable
          head={[t('technical.colIndicator'), t('technical.colValue'), t('technical.colEval')]}
          rows={[
            [t('technical.indRsi'), fmtNum(i.rsi), st.rsiZone === 'overbought' ? t('technical.evalOverbought') : st.rsiZone === 'oversold' ? t('technical.evalOversold') : t('technical.evalNeutral')],
            [t('technical.indMacd'), `${fmtNum(i.macdLine, 2)} / sig ${fmtNum(i.macdSignal, 2)}`, st.macdStateVi || '—'],
            [t('technical.indMacdHist'), fmtNum(i.macdHistogram, 2), i.macdHistogram > 0 ? t('technical.evalPositive') : i.macdHistogram < 0 ? t('technical.evalNegative') : '—'],
            [t('technical.indStoch'), `${fmtNum(i.stochK)} / ${fmtNum(i.stochD)}`, evalOverboughtOversoldNeutral(i.stochK > 80, i.stochK < 20)],
            [t('technical.indWilliams'), fmtNum(i.williamsR), evalOverboughtOversoldNeutral(i.williamsR > -20, i.williamsR < -80)],
            [t('technical.indCci'), fmtNum(i.cci), evalOverboughtOversoldNeutral(i.cci > 100, i.cci < -100)],
            [t('technical.indAdx'), `${fmtNum(i.adx)} (+DI ${fmtNum(i.plusDI)} / −DI ${fmtNum(i.minusDI)})`, st.adxRegimeVi || '—'],
            [t('technical.indEma'), `${yesNo(t, st.aboveEma50_1d)} / ${yesNo(t, st.aboveEma200_1d)}`, st.emaCrossVi || '—'],
            [t('technical.indIchimoku'), ICHI_KEY[st.ichimokuPosition] ? t(ICHI_KEY[st.ichimokuPosition]) : '—', t('technical.meaningIchimoku')],
            [t('technical.indObv'), st.obvTrendVi || '—', t('technical.meaningObv')],
            [t('technical.indVwap'), `$${fmtPrice(i.vwap)}`, rel(i.vwap)],
            [t('technical.indAtrDaily'), `$${fmtPrice(i.atr)}`, t('technical.meaningAtrDaily')],
          ]}
        />
      </ReportSection>

      {/* 6 — Liquidity health (order book) + trade flow */}
      {(liq || fl || of) && (
        <ReportSection no={6} title={t('technical.section6Title')}>
          {liq && (
            <div style={{ marginBottom: fl ? 12 : 0 }}>
              <div style={s.liqHealthRow}>
                {t('technical.liqEvalLabel')}{' '}
                <span style={{ ...s.liqBadge, color: HEALTH_COLOR[liq.health] || 'var(--text)', borderColor: (HEALTH_COLOR[liq.health] || '#888') + '55', background: (HEALTH_COLOR[liq.health] || '#888') + '14' }}>
                  {liq.healthVi}
                </span>
              </div>
              <DataTable
                head={[t('technical.colOrderBook'), t('technical.colValue'), t('technical.colMeaning')]}
                rows={[
                  [t('technical.rowSpread'), `${fmtNum(liq.spreadPct, 3)}%`, liq.spreadPct <= 0.1 ? t('technical.meaningSpreadNarrow') : t('technical.meaningSpreadWide')],
                  [t('technical.rowDepth1pct'), `${fmtUsdC(liq.bidDepth1pctUsd)} / ${fmtUsdC(liq.askDepth1pctUsd)}`, t('technical.meaningDepth1pct')],
                  [t('technical.rowImbalance2pct'), fmtNum(liq.depthImbalance2pct, 3), liq.depthImbalance2pct > 0.15 ? t('technical.meaningBuyHeavier') : liq.depthImbalance2pct < -0.15 ? t('technical.meaningSellHeavier') : t('technical.meaningBalanced')],
                  [t('technical.rowTotalDepth2pct'), fmtUsdC(liq.totalDepth2pctUsd), t('technical.meaningTotalDepth')],
                ]}
              />
            </div>
          )}
          {fl && (
            <DataTable
              head={[t('technical.colFlow1d'), t('technical.colValue'), t('technical.colMeaning')]}
              rows={[
                [t('technical.rowTotalVolume'), `${fmtNum(fl.totalVolume, 0)} (${fmtUsdC(fl.quoteVolume)})`, t('technical.meaningTotalVolume')],
                [t('technical.rowBuySellVolume'), `${fmtNum(fl.buyVolume, 0)} / ${fmtNum(fl.sellVolume, 0)}`, t('technical.meaningBuySellVolume')],
                [t('technical.rowVolumeImbalance'), fmtNum(fl.volumeImbalance, 3), fl.volumeImbalance > 0.05 ? t('technical.meaningBuyActive') : fl.volumeImbalance < -0.05 ? t('technical.meaningSellActive') : t('technical.meaningBalanced')],
                [t('technical.rowTradeCount'), fl.tradeCount != null ? fl.tradeCount.toLocaleString('en-US') : '—', t('technical.meaningTradeCount')],
              ]}
            />
          )}
          {fl && <WashCostBox fl={fl} />}
          {of && (
            <div style={{ marginTop: 12 }}>
              <DataTable
                head={[t('technical.colOrdersRecent'), t('technical.colValue'), t('technical.colMeaning')]}
                rows={[
                  [t('technical.rowBuySellOrders'), `${(of.buyOrders ?? 0).toLocaleString('en-US')} / ${(of.sellOrders ?? 0).toLocaleString('en-US')}`, t('technical.meaningBuySellOrders')],
                  [t('technical.rowOrderImbalance'), fmtNum(of.orderImbalance, 3), of.orderImbalance > 0.05 ? t('technical.meaningMoreBuy') : of.orderImbalance < -0.05 ? t('technical.meaningMoreSell') : t('technical.meaningBalanced')],
                  [t('technical.rowSampleSize'), of.sampleSize != null ? t('technical.meaningSampleSize', { count: of.sampleSize.toLocaleString('en-US') }) : '—', t('technical.meaningSampleWindow')],
                ]}
              />
            </div>
          )}
        </ReportSection>
      )}

      {/* 7 — Watch points (replaces the scenarios section) */}
      {nar.watchPoints && (
        <ReportSection no={7} title={t('technical.section7Title')} commentary={nar.watchPoints} />
      )}

      {/* 7 — Disclaimer (shared component, consistent across features) */}
      <Disclaimer />
    </div>
  );
}

// ── Tab 3: historical indicator charts ──────────────────────────────────
// 3 evenly-spaced ticks (min/mid/max) — simple and always fits even on the
// shortest (70-90px) factor charts, unlike a "nice round number" axis
// algorithm that can produce an inconsistent tick count.
function yTicks(minY, maxY) {
  const mid = (minY + maxY) / 2;
  return [maxY, mid, minY];
}
const fmtTick = (v) => (Number.isInteger(v) ? `${v}` : v.toFixed(1));

// Generic multi-series SVG line chart, scaled to 100% container width via
// viewBox + preserveAspectRatio="none" (no horizontal scroll needed, unlike
// StakingPage's day-bar chart which intentionally allows one) — a smooth
// line series reads better stretched to fit than clipped/scrolled. Left
// padding reserves room for the Y-axis value labels so a reader can tell
// what a line's height actually means, not just its shape.
function MiniLineChart({ series, height = 100, yMin = null, yMax = null }) {
  const allPoints = series.flatMap((sr) => sr.points);
  if (allPoints.length === 0) return null;
  const xs = [...new Set(allPoints.map((p) => p.x))].sort();
  const ys = allPoints.map((p) => p.y);
  const maxY = yMax != null ? yMax : Math.max(...ys);
  const minY = yMin != null ? yMin : Math.min(...ys);
  const rangeY = (maxY - minY) || 1;
  const W = 720, padL = 26, padR = 4, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const xIndex = new Map(xs.map((x, i) => [x, i]));
  const xPos = (x) => padL + (xs.length > 1 ? (xIndex.get(x) / (xs.length - 1)) * plotW : plotW / 2);
  const yPos = (y) => padT + plotH - ((y - minY) / rangeY) * plotH;
  const labelEvery = Math.max(1, Math.ceil(xs.length / 6));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {yTicks(minY, maxY).map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{fmtTick(v)}</text>
        </g>
      ))}
      {series.map((sr) => {
        const byX = new Map(sr.points.map((p) => [p.x, p]));
        const present = xs.filter((x) => byX.has(x));
        if (present.length === 0) return null;
        const path = present.map((x, i) => `${i === 0 ? 'M' : 'L'}${xPos(x).toFixed(1)},${yPos(byX.get(x).y).toFixed(1)}`).join(' ');
        return <path key={sr.name} d={path} fill="none" stroke={sr.color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />;
      })}
      {/* Invisible, generously-sized hit circles per point — native <title>
          tooltip on hover shows the exact date/value, since the line itself
          has no discrete markers to hover. */}
      {series.map((sr) => sr.points.map((p) => (
        <circle key={`${sr.name}-${p.x}`} cx={xPos(p.x)} cy={yPos(p.y)} r={7} fill="transparent" stroke="none">
          <title>{p.x}: {fmtTick(p.y)}</title>
        </circle>
      )))}
      {xs.map((x, i) => (i % labelEvery === 0 ? (
        <text key={x} x={xPos(x)} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{x.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

// Split-area chart for the Green/Red zone score: a single zigzag boundary
// line (the green value each day) with red filled ABOVE it and green filled
// BELOW it — green+red always sum to 100 by construction (see
// zoneScoreService.js), so the boundary line's height directly IS the
// green/red split for that day, not two separately-scaled series.
function ZoneAreaChart({ rows, height = 140 }) {
  if (rows.length === 0) return null;
  const W = 720, padL = 26, padR = 4, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const xPos = (i) => padL + (rows.length > 1 ? (i / (rows.length - 1)) * plotW : plotW / 2);
  const yPos = (v) => padT + plotH - (v / 100) * plotH; // 0 -> bottom, 100 -> top
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));

  const linePts = rows.map((r, i) => [xPos(i), yPos(r.green)]);
  const lineD = linePts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const firstX = linePts[0][0], lastX = linePts[linePts.length - 1][0];
  // Red area: boundary line, then close along the top edge back to the start.
  const redAreaD = `${lineD} L${lastX.toFixed(1)},${padT} L${firstX.toFixed(1)},${padT} Z`;
  // Green area: boundary line, then close along the bottom edge back to the start.
  const bottomY = padT + plotH;
  const greenAreaD = `${lineD} L${lastX.toFixed(1)},${bottomY} L${firstX.toFixed(1)},${bottomY} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[100, 50, 0].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke={v === 50 ? 'var(--text-dim)' : 'var(--border)'} strokeWidth={1} strokeDasharray={v === 50 ? '2,2' : '3,3'} />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{v}</text>
        </g>
      ))}
      <path d={redAreaD} fill="#ef4444" opacity={0.35} stroke="none" />
      <path d={greenAreaD} fill="#10b981" opacity={0.35} stroke="none" />
      <path d={lineD} fill="none" stroke="#334155" strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      {/* Invisible hit circles on the boundary line — native <title> tooltip
          shows the exact Green/Red split for that day on hover. */}
      {rows.map((r, i) => (
        <circle key={r.date} cx={xPos(i)} cy={yPos(r.green)} r={7} fill="transparent" stroke="none">
          <title>{r.date}: Green {r.green} / Red {r.red}</title>
        </circle>
      ))}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i)} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

// Compact number formatter for the volume axis (raw token units, not USD —
// can range from low-thousands for a low-cap to billions for a high-supply
// token, so K/M/B compaction matters here the same way it does for fmtUsdC).
function fmtVolTick(v) {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

const VOLUME_FLOW_SERIES = [
  { key: 'total', color: '#64748b' },
  { key: 'buy', color: '#10b981' },
  { key: 'sell', color: '#ef4444' },
];
const VOLUME_FLOW_LABEL = { total: 'Tổng KL', buy: 'KL mua', sell: 'KL bán' };

// Grouped bar chart — 3 bars per date (total/buy/sell), plus one connecting
// line per series threading through that series' bar tops across dates, so
// a reader can track each series' trend the same way a line chart would
// while still seeing the per-day 3-way split as bars. Y-axis gridlines
// mirror MiniLineChart/ZoneAreaChart's convention (3 evenly-spaced ticks).
function VolumeFlowChart({ rows, height = 160 }) {
  if (rows.length === 0) return null;
  const W = 720, padL = 34, padR = 4, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const maxY = Math.max(1, ...rows.flatMap((r) => VOLUME_FLOW_SERIES.map((s) => r[s.key]).filter(Number.isFinite)));
  const groupW = plotW / rows.length;
  const barGap = 1.5;
  const barW = Math.max(1, (groupW - barGap * 4) / 3);
  const groupContentW = barW * 3 + barGap * 2;
  const groupLeftPad = (groupW - groupContentW) / 2;
  const xPos = (i) => padL + i * groupW;
  const yPos = (v) => padT + plotH - (Math.max(0, v) / maxY) * plotH;
  const barX = (i, si) => xPos(i) + groupLeftPad + si * (barW + barGap);
  const barCenterX = (i, si) => barX(i, si) + barW / 2;
  const baseY = yPos(0);
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {yTicks(0, maxY).map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{fmtVolTick(v)}</text>
        </g>
      ))}
      {rows.map((r, i) => VOLUME_FLOW_SERIES.map((sr) => {
        const v = r[sr.key];
        if (!Number.isFinite(v)) return null;
        const x = barX(i, VOLUME_FLOW_SERIES.indexOf(sr));
        const y = yPos(v);
        return (
          <rect key={`${r.date}-${sr.key}`} x={x} y={y} width={barW} height={Math.max(0, baseY - y)} fill={sr.color} opacity={0.85}>
            <title>{r.date} — {VOLUME_FLOW_LABEL[sr.key]}: {fmtVolTick(v)}</title>
          </rect>
        );
      }))}
      {VOLUME_FLOW_SERIES.map((sr, si) => {
        const pts = rows.map((r, i) => (Number.isFinite(r[sr.key]) ? [barCenterX(i, si), yPos(r[sr.key])] : null)).filter(Boolean);
        if (pts.length < 2) return null;
        const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
        return <path key={sr.key} d={d} fill="none" stroke={sr.color} strokeWidth={1.4} strokeLinejoin="round" strokeLinecap="round" />;
      })}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i) + groupW / 2} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

const FACTOR_COLOR = {
  oversold: '#8b5cf6', support: '#3b82f6', bounceRate: '#f59e0b',
  volumeFlow: '#06b6d4', exchangeFlow: '#ec4899', orderBook: '#14b8a6',
};

// Overbought/oversold oscillator (RSI, Stochastic %K, Williams %R, CCI) —
// a line chart where each point is colored by which zone it falls in
// (same thresholds the rest of the app already uses), so a reader can spot
// overbought/oversold stretches at a glance without reading the axis.
// Rendered small and stacked directly under one another (via s.factorGrid,
// the same vertical-stack layout used for the zone-score factor charts) so
// the four can be compared date-for-date.
const OSCILLATOR_ZONE_COLOR = { overbought: '#ef4444', neutral: '#eab308', oversold: '#10b981' };
function oscillatorZone(v, obThreshold, osThreshold) {
  return v > obThreshold ? 'overbought' : v < osThreshold ? 'oversold' : 'neutral';
}
function OscillatorChart({ rows, obThreshold, osThreshold, min, max, height = 90 }) {
  if (rows.length === 0) return null;
  const vals = rows.map((r) => r.value);
  const minY = min != null ? min : Math.min(...vals, osThreshold);
  const maxY = max != null ? max : Math.max(...vals, obThreshold);
  const rangeY = (maxY - minY) || 1;
  const W = 720, padL = 30, padR = 4, padT = 6, padB = 16;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const xPos = (i) => padL + (rows.length > 1 ? (i / (rows.length - 1)) * plotW : plotW / 2);
  const yPos = (v) => padT + plotH - ((v - minY) / rangeY) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));
  const lineD = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xPos(i).toFixed(1)},${yPos(r.value).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[obThreshold, osThreshold].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{v}</text>
        </g>
      ))}
      <path d={lineD} fill="none" stroke="var(--text-dim)" strokeWidth={1} strokeOpacity={0.5} />
      {rows.map((r, i) => {
        const zone = oscillatorZone(r.value, obThreshold, osThreshold);
        const color = OSCILLATOR_ZONE_COLOR[zone];
        return (
          <g key={r.date}>
            <circle cx={xPos(i)} cy={yPos(r.value)} r={7} fill="transparent" stroke="none">
              <title>{r.date}: {r.value.toFixed(1)} ({zone})</title>
            </circle>
            <circle cx={xPos(i)} cy={yPos(r.value)} r={2.5} fill={color} pointerEvents="none" />
          </g>
        );
      })}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i)} y={height - 3} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

function ChartsView({ items, symbol, onSelect, data, ready }) {
  const { t } = useTranslation();

  const compositeSeries = [{
    name: 'composite', color: 'var(--accent)',
    points: data.filter((d) => d.composite != null).map((d) => ({ x: d.date, y: d.composite })),
  }];

  const zoneRows = data
    .filter((d) => d.zoneScore?.green != null)
    .map((d) => ({ date: d.date, green: d.zoneScore.green, red: d.zoneScore.red }));

  const volumeRows = data
    .filter((d) => d.flow?.totalVolume != null)
    .map((d) => ({ date: d.date, total: d.flow.totalVolume, buy: d.flow.buyVolume, sell: d.flow.sellVolume }));

  // Overbought/oversold oscillators — same thresholds already used
  // elsewhere in the app (DetailPanel's indicator table, zoneScoreService's
  // oversoldFraction, and structure.rsiZone), reused here rather than
  // invented fresh so a "overbought" label means the same thing everywhere.
  const buildOscillatorRows = (key) => data
    .filter((d) => d.indicators?.d1?.[key] != null)
    .map((d) => ({ date: d.date, value: d.indicators.d1[key] }));
  const OSCILLATORS = [
    { key: 'rsi', label: t('technical.indRsi'), obThreshold: 70, osThreshold: 30, min: 0, max: 100 },
    { key: 'stochK', label: t('technical.indStoch'), obThreshold: 80, osThreshold: 20, min: 0, max: 100 },
    { key: 'williamsR', label: t('technical.indWilliams'), obThreshold: -20, osThreshold: -80, min: -100, max: 0 },
    { key: 'cci', label: t('technical.indCci'), obThreshold: 100, osThreshold: -100, min: null, max: null },
  ].map((o) => ({ ...o, rows: buildOscillatorRows(o.key) })).filter((o) => o.rows.length > 0);

  // Only factors with at least one real (non-N/A) reading anywhere in the
  // window get their own chart — a factor that's N/A every single day
  // (e.g. exchangeFlow for a pair with no coverage) is skipped entirely
  // rather than showing an empty chart.
  const factorPointsByName = {};
  for (const d of data) {
    for (const f of d.zoneScore?.factors || []) {
      if (f.na || f.fraction == null) continue;
      if (!factorPointsByName[f.name]) factorPointsByName[f.name] = [];
      factorPointsByName[f.name].push({ x: d.date, y: Math.round(f.fraction * 100) });
    }
  }
  const factorNames = Object.keys(factorPointsByName);

  return (
    <div style={s.layout}>
      <aside style={s.sidebar}>
        <div style={s.sideList}>
          {items.map((d) => {
            const active = d.symbol === symbol;
            return (
              <button
                key={d.symbol}
                style={active ? s.sideItemActive : s.sideItem}
                onClick={(e) => { onSelect(d.symbol); e.currentTarget.blur(); }}
              >
                <span style={s.sideTop}>
                  <span style={s.sideTicker}>{d.base || d.symbol}</span>
                  <SideScore d={d} />
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <main style={s.main}>
        <div style={{ padding: '18px 20px' }}>
          {!ready ? (
            <div style={s.loading}>{t('technical.loadingData')}</div>
          ) : data.length === 0 ? (
            <div style={s.loading}>{t('technical.chartsNoData')}</div>
          ) : (
            <>
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '0 0 14px' }}>{t('technical.chartRangeNote')}</p>

              <div style={s.chartCard}>
                <div style={s.chartTitle}>{t('technical.chartComposite')}</div>
                <MiniLineChart series={compositeSeries} yMin={0} yMax={100} height={110} />
              </div>

              {zoneRows.length > 0 && (
                <div style={s.chartCard}>
                  <div style={s.chartTitle}>{t('technical.chartZoneTitle')}</div>
                  <ZoneAreaChart rows={zoneRows} height={140} />
                  <div style={s.chartLegend}>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>■ Red</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>■ Green</span>
                  </div>
                </div>
              )}

              {volumeRows.length > 0 && (
                <div style={s.chartCard}>
                  <div style={s.chartTitle}>{t('technical.chartVolumeFlowTitle')}</div>
                  <VolumeFlowChart rows={volumeRows} height={160} />
                  <div style={s.chartLegend}>
                    <span style={{ color: '#64748b', fontWeight: 700 }}>■ {t('technical.legendTotalVolume')}</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>■ {t('technical.legendBuyVolume')}</span>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>■ {t('technical.legendSellVolume')}</span>
                  </div>
                </div>
              )}

              {OSCILLATORS.length > 0 && (
                <div style={s.chartCard}>
                  <div style={s.chartTitle}>{t('technical.chartOscillatorsTitle')}</div>
                  <div style={s.factorGrid}>
                    {OSCILLATORS.map((o) => (
                      <div key={o.key}>
                        <div style={s.factorChartTitle}>{o.label}</div>
                        <OscillatorChart rows={o.rows} obThreshold={o.obThreshold} osThreshold={o.osThreshold} min={o.min} max={o.max} height={90} />
                      </div>
                    ))}
                  </div>
                  <div style={s.chartLegend}>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>● {t('technical.evalOverbought')}</span>
                    <span style={{ color: '#eab308', fontWeight: 700 }}>● {t('technical.evalNeutral')}</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>● {t('technical.evalOversold')}</span>
                  </div>
                </div>
              )}

              {factorNames.length > 0 && (
                <div style={s.chartCard}>
                  <div style={s.chartTitle}>{t('technical.chartFactorsTitle')}</div>
                  <div style={s.factorGrid}>
                    {factorNames.map((name) => (
                      <div key={name} style={s.factorChartBox}>
                        <div style={s.factorChartTitle}>{ZONE_FACTOR_KEY[name] ? t(ZONE_FACTOR_KEY[name]) : name}</div>
                        <MiniLineChart
                          series={[{ name, color: FACTOR_COLOR[name] || 'var(--accent)', points: factorPointsByName[name] }]}
                          yMin={0} yMax={100} height={90}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  notice: {
    fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
    marginBottom: 16, lineHeight: 1.6,
  },

  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  tabBar: { display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--border)' },
  // Two fully self-contained objects, always used as a straight either/or
  // replacement (never spread/merged) — a spread like {...tabBtn,
  // ...(active ? tabBtnActive : {})} leaves a previously-active tab's
  // underline/color stuck on it after it goes back to inactive.
  tabBtn: {
    fontSize: 13, fontWeight: 500, color: 'var(--text-dim)', background: 'transparent',
    border: 'none', outline: 'none', boxShadow: 'none', textDecoration: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: 'transparent',
    padding: '9px 4px', marginBottom: -1, cursor: 'pointer',
  },
  tabBtnActive: {
    fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'transparent',
    border: 'none', outline: 'none', boxShadow: 'none', textDecoration: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: 'var(--accent)',
    padding: '9px 4px', marginBottom: -1, cursor: 'pointer',
  },

  filterLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  histDateBar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  histDateSelect: {
    fontSize: 13, fontWeight: 600, color: 'var(--text)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', outline: 'none', cursor: 'pointer',
  },

  flowStrip: {
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '10px 14px', margin: '12px 0', fontSize: 12,
  },
  flowStripTitle: { fontWeight: 700, color: 'var(--text)' },
  flowStripItem: { color: 'var(--text-dim)' },
  flowStripNote: { fontSize: 10.5, color: 'var(--text-muted)', marginLeft: 'auto' },

  zonePanel: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '12px 14px', margin: '12px 0', fontSize: 12,
  },
  zoneHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' },
  zoneBadge: { fontSize: 10.5, fontWeight: 800, border: '1px solid', borderRadius: 8, padding: '3px 9px', letterSpacing: 0.3 },
  zoneBarTrack: { display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'var(--border)' },
  zoneBarFill: { transition: 'width 0.2s' },
  zoneBarLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 6, marginBottom: 10 },
  zoneRiskBox: {
    fontSize: 11.5, color: '#b45309', background: '#f59e0b14', border: '1px solid #f59e0b55',
    borderRadius: 8, padding: '8px 10px', marginBottom: 10, lineHeight: 1.5,
  },
  zoneFactors: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 },
  zoneFactorRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  zoneFactorLabel: { fontSize: 11, color: 'var(--text-muted)', flex: '1 1 180px', minWidth: 140 },
  zoneFactorTrack: { flex: 1, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', display: 'block' },
  zoneFactorFill: { display: 'block', height: '100%', background: 'var(--accent)' },

  // ── Sidebar + main layout ───────────────────────────────────────────────
  layout: { display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' },
  sidebar: {
    flex: '0 0 240px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 10,
    position: 'sticky', top: 12,
  },
  sideSearch: {
    background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 12px', fontSize: 13, outline: 'none',
  },
  sideList: { display: 'flex', flexDirection: 'column', gap: 6 },
  // Two fully self-contained objects, always used as a straight either/or
  // replacement (never spread/merged) — a spread like {...sideItem,
  // ...(active ? sideItemActive : {})} leaves the accent border/shadow
  // stuck on a previously-active item after it goes back to inactive.
  sideItem: {
    display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', cursor: 'pointer',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, outline: 'none',
    padding: '10px 12px', font: 'inherit', color: 'inherit', transition: 'all 0.15s', boxShadow: 'none',
  },
  sideItemActive: {
    display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', cursor: 'pointer',
    background: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 10, outline: 'none',
    padding: '10px 12px', font: 'inherit', color: 'inherit', transition: 'all 0.15s', boxShadow: '0 0 0 1px var(--accent)',
  },
  sideTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  sideTicker: { fontSize: 14, fontWeight: 800, color: 'var(--text)' },
  sideScoreGroup: { display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0 },
  insufficientBanner: { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.45)', color: 'var(--text)', borderRadius: 12, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.55, display: 'flex', flexDirection: 'column', gap: 4 },
  sideScore: { fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  sideZone: { fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' },
  sideBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11 },
  sidePrice: { color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' },
  sideEmpty: { fontSize: 12, color: 'var(--text-muted)', padding: '12px 4px', textAlign: 'center' },
  main: {
    flex: '1 1 520px', minWidth: 0, background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden',
  },

  // ── Tab 3: historical charts ─────────────────────────────────────────────
  chartCard: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 },
  chartTitle: { fontSize: 12.5, fontWeight: 700, color: 'var(--text)', marginBottom: 10 },
  chartLegend: { display: 'flex', gap: 16, fontSize: 11, marginTop: 8 },
  // One chart per row — a multi-column grid squeezed each chart too narrow
  // for its date labels (e.g. "08-13") to render legibly.
  factorGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
  factorChartBox: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' },
  factorChartTitle: { fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 },

  // ── Report layout (docx-style) ──────────────────────────────────────────
  report: { padding: '18px 20px 22px', maxWidth: 900 },
  narrativeMissing: {
    fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg)',
    border: '1px dashed var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16,
  },

  section: { marginBottom: 22 },
  sectionHeading: {
    fontSize: 13, fontWeight: 800, color: 'var(--text)', letterSpacing: 0.3,
    paddingBottom: 6, borderBottom: '2px solid var(--accent)', marginBottom: 10,
  },
  sectionText: { margin: '0 0 10px', fontSize: 13, lineHeight: 1.8, color: 'var(--text)', textAlign: 'justify' },
  liqHealthRow: { fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 },
  liqBadge: { fontSize: 12, fontWeight: 800, border: '1px solid', borderRadius: 7, padding: '2px 10px', whiteSpace: 'nowrap' },

  washBox: {
    marginTop: 12, background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '10px 12px',
  },
  washTitle: { fontSize: 11.5, fontWeight: 800, color: 'var(--text)', marginBottom: 4 },
  washDesc: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 8px' },
  washRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: 12.5 },
  washLabel: { color: 'var(--text-dim)' },
  washValue: { fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },
  washNote: { fontSize: 10, color: 'var(--text-dim)', fontStyle: 'italic', margin: '8px 0 0' },
  summaryLine: {
    fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
  },

  // Score bars
  scoreRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' },
  scoreRowHi: { fontWeight: 800 },
  scoreLabel: { flex: '0 0 150px', fontSize: 12, color: 'var(--text)', minWidth: 0 },
  scoreWeight: { color: 'var(--text-dim)', fontSize: 10 },
  scoreTrack: { flex: 1, height: 12, background: 'var(--border)', borderRadius: 6, overflow: 'hidden', minWidth: 0 },
  scoreFill: { display: 'block', height: '100%', borderRadius: 6 },
  scoreVal: { flex: '0 0 34px', textAlign: 'right', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  formula: { fontSize: 11, color: 'var(--text-dim)', marginTop: 8, fontStyle: 'italic' },

  // Data tables
  dtWrap: { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginTop: 4 },
  dtRow: { display: 'flex', borderBottom: '1px solid var(--border)' },
  dtRowHi: { background: 'var(--bg)' },
  dtHead: { background: 'var(--bg)' },
  dtCell: {
    flex: 1, fontSize: 12, color: 'var(--text-dim)', padding: '8px 12px',
    minWidth: 0, wordBreak: 'break-word', borderLeft: '1px solid var(--border)',
  },
  dtCellFirst: { flex: '0 0 34%', color: 'var(--text)', fontWeight: 600, borderLeft: 'none' },
  dtCellVal: { flex: '0 0 24%', color: 'var(--text)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },

  disclaimer: { margin: 0, fontSize: 11, lineHeight: 1.7, color: 'var(--text-muted)', fontStyle: 'italic' },
  narrativeMeta: {
    fontSize: 10, color: 'var(--text-dim)', borderTop: '1px solid var(--border)',
    paddingTop: 8, marginTop: 10,
  },
};
